import React, { InputHTMLAttributes, forwardRef, Ref } from 'react';
import { UseFormRegister, FieldErrors, FieldValues, RegisterOptions, Path } from 'react-hook-form';

// Original Input props for React Hook Form integration
interface InputProps<T extends FieldValues = FieldValues> extends Omit<InputHTMLAttributes<HTMLInputElement>, 'name'> {
  label?: string;
  error?: string;
  fullWidth?: boolean;
  id: string;
  name: Path<T>;
  register: UseFormRegister<T>;
  errors: FieldErrors<T>;
  validationRules?: RegisterOptions<T, Path<T>>;
}

// Simplified Input props for direct use without React Hook Form
export interface SimpleInputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  fullWidth?: boolean;
}

/**
 * Input Component
 *
 * A reusable generic input component with label and error message
 * Integrated with React Hook Form
 */
function InputInner<T extends FieldValues = FieldValues>(
  { label, error, fullWidth = true, className = '', register, name, validationRules, errors, ...props }: InputProps<T>,
  ref: Ref<HTMLInputElement>
) {
  // Get error message from form errors
  const errorMessage = (errors[name]?.message as string) || error;

  // Base classes
  // text-base (16px) on mobile prevents iOS focus-zoom; py-2.5 hits a 44px target.
  // sm: restores the compact 14px/py-2 desktop look.
  const baseClasses = 'rounded-lg border px-3 py-2.5 text-base sm:py-2 sm:text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-0';

  // Error classes
  const errorClasses = errorMessage
    ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
    : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500';

  // Width classes
  const widthClasses = fullWidth ? 'w-full' : '';

  return (
    <div className={fullWidth ? 'w-full' : ''}>
      {label && (
        <label htmlFor={props.id} className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}
      <input
        {...register(name, validationRules)}
        className={`${baseClasses} ${errorClasses} ${widthClasses} ${className}`}
        {...props}
      />
      {errorMessage && (
        <p className="mt-1 text-sm text-red-600">{errorMessage}</p>
      )}
    </div>
  );
}

// forwardRef wrapper with generic support via type assertion
const Input = forwardRef(InputInner) as <T extends FieldValues = FieldValues>(
  props: InputProps<T> & { ref?: Ref<HTMLInputElement> }
) => React.ReactElement | null;

/**
 * SimpleInput Component
 *
 * A simplified version of Input that doesn't require React Hook Form
 */
export const SimpleInput: React.FC<SimpleInputProps> = ({
  label,
  error,
  fullWidth = true,
  className = '',
  ...props
}) => {
  // Base classes
  // text-base (16px) on mobile prevents iOS focus-zoom; py-2.5 hits a 44px target.
  // sm: restores the compact 14px/py-2 desktop look.
  const baseClasses = 'rounded-lg border px-3 py-2.5 text-base sm:py-2 sm:text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-0';

  // Error classes
  const errorClasses = error
    ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
    : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500';

  // Width classes
  const widthClasses = fullWidth ? 'w-full' : '';

  return (
    <div className={fullWidth ? 'w-full' : ''}>
      {label && (
        <label htmlFor={props.id} className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}
      <input
        className={`${baseClasses} ${errorClasses} ${widthClasses} ${className}`}
        {...props}
      />
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};

export default Input;
